import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog/confirm-delete-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from '@app-core/services/data/data.service';

export interface TagNode {
  tagId: number;
  tagName: string;
  rootTagId: number;
  parentTagId: number | null;
  tagStatus: string;
  entityName: string;
  path: string | null;
  children: TagNode[];
}

export interface FlatTagNode {
  tagName: string;
  rootTagId: number;
  parentTagId: number | null;
  level: number;
  hasChildren: boolean;
  childCount: number;
  tagStatus: string;
}

export interface AttributeItem {
  attributeName: string;
  attributeId: number;
  attributeType: string;
  status: string;
  tags: any[];
  tagTree: TagNode[];
}

@Component({
  selector: 'app-user-assign-tags-group',
  templateUrl: './user-assign-tags-group.component.html',
  styleUrls: ['./user-assign-tags-group.component.scss'],
})
export class UserAssignTagsGroupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() groupIndex: number = 0;
  @Input() attributeNames: AttributeItem[] = [];
  @Output() removeGroup = new EventEmitter<void>();
  @Output() groupRuleChange = new EventEmitter<any>();
  @Input() existingRule: any = null;

  formArray: FormArray;
  conditionType: 'AND' | 'OR' = 'OR';
  selectedAttributes: string[] = [];
  expandedTags: { [rowIndex: number]: Set<string> } = {};

  // ── Custom tag panel state ──────────────────────────────────────────────────
  // Use string key instead of number so that index 0 is never falsy
  activePanelRowIndex: number | null = null;
  tagPanelBreadcrumb: { [rowIndex: number]: { tagName: string; attributeName: string; node: TagNode }[] } = {};
  tagSearchQuery: { [rowIndex: number]: string } = {};
  private _documentClickHandler: () => void;

  // ── Loop-prevention guards ──────────────────────────────────────────────────
  private _isHydrating = false;
  private _lastHydratedRule: any = null;
  tagPanelDepth: { [key: number]: number } = {};
  isAddConditionMenuOpen = false;
  openGroupConnectorIndex: number = -1;

  selectedConditionType: 'AND' | 'OR' = 'AND';
  constructor(
    private fb: FormBuilder,
    private snackbarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog, // ← add
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    public dataService: DataService
  ) {
    this.formArray = this.fb.array([]);
  }

  ngOnInit(): void {
    if (!this.existingRule) {
      this.addCondition();
    }

    // KEY FIX: use setTimeout so the document click handler fires AFTER Angular's
    // current event cycle completes. Without this, clicking the trigger field
    // dispatches a document click that immediately closes the panel that was
    // just opened by openTagPanel().
    this._documentClickHandler = () => {
      setTimeout(() => {
        this.activePanelRowIndex = null;
        this.cdr.detectChanges();
      }, 0);
    };
    document.addEventListener('click', this._documentClickHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._documentClickHandler);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['attributeNames'] && this.attributeNames?.length && this.existingRule) {
      if (this._lastHydratedRule !== this.existingRule) {
        this._lastHydratedRule = this.existingRule;
        this.formArray.clear();
        this.hydrateFromRule(this.existingRule);
      }
    }
  }

  // ─── Panel open/close helpers ─────────────────────────────────────────────────

  // Explicit method avoids Angular template truthy issues with index 0
  isPanelOpen(rowIndex: number): boolean {
    return this.activePanelRowIndex === rowIndex;
  }

  panelPosition: { top: string; left: string; width: string; bottom?: string } = {
    top: 'auto',
    left: '0px',
    width: '300px',
  };

  openTagPanel(event: Event, group: FormGroup, rowIndex: number) {
    event.stopPropagation();
    const attribute = group.get('myControl')?.value;
    if (!attribute) {
      this.snackbarService.failure(this.translate.instant('Please select an attribute first'));
      return;
    }
    if (this.activePanelRowIndex === rowIndex) {
      this.activePanelRowIndex = null;
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const panelHeight = 380;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < panelHeight && spaceAbove > spaceBelow;

    this.panelPosition = {
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      top: openUpward ? 'auto' : `${rect.bottom + 4}px`,
      bottom: openUpward ? `${window.innerHeight - rect.top + 4}px` : undefined,
    };

    this.activePanelRowIndex = rowIndex;
    this.tagPanelBreadcrumb[rowIndex] = [];
    this.tagSearchQuery[rowIndex] = '';
    this.cdr.detectChanges();
  }
  private hydrateFromRule(rule: any) {
    if (this._isHydrating) return;
    this._isHydrating = true;

    this.conditionType = rule.groupOp || 'AND';

    (rule.rows || []).forEach((row: any) => {
      const attrObj = this.attributeNames.find((a) => String(a.attributeId) === String(row.attributeId));
      if (!attrObj) return;

      const group = this.createConditionGroup();
      this.formArray.push(group);

      group.patchValue({ myControl: attrObj.attributeName }, { emitEvent: false });

      const rowIndex = this.formArray.controls.indexOf(group);
      this.expandedTags[rowIndex] = new Set<string>();
      const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, rowIndex);
      group.patchValue({ flatTags }, { emitEvent: false });

      const selectedTagNames = (row.tagIds || [])
        .map((id: number) => attrObj.tags.find((t: any) => t.tagId === id)?.tagName)
        .filter(Boolean);

      group.patchValue({ toppings: selectedTagNames }, { emitEvent: false });
    });

    this.refreshFilters();
    this._isHydrating = false;
    this.emitGroupRule();
  }

  // ─── Option list ─────────────────────────────────────────────────────────────

  get options(): string[] {
    return this.attributeNames.filter((a) => a.status?.toUpperCase() === 'ACTIVE').map((a) => a.attributeName);
  }

  // ─── Tree helpers ─────────────────────────────────────────────────────────────

  getAttrObj(attributeName: string): AttributeItem | undefined {
    return this.attributeNames.find((a) => a.attributeName === attributeName);
  }

  private flattenTreeWithExpansion(nodes: TagNode[], level: number, rowIndex: number): FlatTagNode[] {
    const result: FlatTagNode[] = [];
    nodes.forEach((node) => {
      result.push({
        tagName: node.tagName,
        rootTagId: node.rootTagId,
        parentTagId: node.parentTagId,
        level,
        hasChildren: node.children.length > 0,
        childCount: node.children.length,
        tagStatus: node.tagStatus,
      });
      if (node.children.length > 0 && this.expandedTags[rowIndex]?.has(node.tagName)) {
        result.push(...this.flattenTreeWithExpansion(node.children, level + 1, rowIndex));
      }
    });
    return result;
  }

  getAllDescendantNames(node: TagNode): string[] {
    let names: string[] = [];
    node.children.forEach((child) => {
      names.push(child.tagName);
      names = names.concat(this.getAllDescendantNames(child));
    });
    return names;
  }

  findNodeByRootTagId(nodes: TagNode[], rootTagId: number): TagNode | null {
    for (const node of nodes) {
      if (node.rootTagId === rootTagId) return node;
      const found = this.findNodeByRootTagId(node.children, rootTagId);
      if (found) return found;
    }
    return null;
  }

  // ─── Form group factory ───────────────────────────────────────────────────────

  createConditionGroup(): FormGroup {
    const group = this.fb.group({
      myControl: this.fb.control<string>(''),
      toppings: this.fb.control<string[]>([]),
      subtags: this.fb.control<string[]>([]),
      filteredOptions: this.fb.control<Observable<string[]>>(null as any),
      availableTags: this.fb.control<string[]>([]),
      availableSubtags: this.fb.control<string[]>([]),
      flatTags: this.fb.control<FlatTagNode[]>([]),
    });

    const control = group.get('myControl') as FormControl;
    group.patchValue({
      filteredOptions: control.valueChanges.pipe(
        startWith(''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      ),
    });

    return group;
  }

  // ─── Attribute selected ───────────────────────────────────────────────────────

  onAttributeSelected(selectedAttribute: string, group: FormGroup) {
    const attrObj = this.getAttrObj(selectedAttribute);
    if (!attrObj) return;

    const rowIndex = this.formArray.controls.indexOf(group);
    this.expandedTags[rowIndex] = new Set<string>();
    group.patchValue({ toppings: [], subtags: [] }, { emitEvent: false });

    const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, rowIndex);
    group.patchValue({ flatTags }, { emitEvent: false });

    this.tagPanelBreadcrumb[rowIndex] = null;
    this.tagSearchQuery[rowIndex] = '';

    this.updateSelectedAttributes();
    this.refreshFiltersExcept(group);
  }

  // ─── Tag panel navigation ─────────────────────────────────────────────────────

  trackByTagName(_index: number, item: FlatTagNode): string {
    return item.tagName;
  }

  getVisibleTagsForPanel(group: FormGroup, rowIndex: number): FlatTagNode[] {
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return [];

    const breadcrumbs = this.tagPanelBreadcrumb[rowIndex] || [];
    const query = (this.tagSearchQuery[rowIndex] || '').toLowerCase().trim();

    // use last breadcrumb's node children, or root
    const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
    const sourceNodes: TagNode[] = lastCrumb ? lastCrumb.node.children : attrObj.tagTree;

    const flat: FlatTagNode[] = sourceNodes.map((node) => ({
      tagName: node.tagName,
      rootTagId: node.rootTagId,
      parentTagId: node.parentTagId,
      level: 0,
      hasChildren: node.children.length > 0,
      childCount: node.children.length,
      tagStatus: node.tagStatus,
    }));

    if (!query) return flat;
    return flat.filter((f) => f.tagName.toLowerCase().includes(query));
  }

  getChildCount(group: FormGroup, flatNode: FlatTagNode): number {
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return 0;
    const node = this.findNodeByRootTagId(attrObj.tagTree, flatNode.rootTagId);
    return node?.children.length || 0;
  }

  drillInto(rowIndex: number, group: FormGroup, flatNode: FlatTagNode) {
    this.tagPanelDepth[rowIndex] = (this.tagPanelDepth[rowIndex] ?? 0) + 1;
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    if (!attrObj) return;
    const node = this.findNodeByRootTagId(attrObj.tagTree, flatNode.rootTagId);
    if (!node) return;

    if (!this.tagPanelBreadcrumb[rowIndex]) {
      this.tagPanelBreadcrumb[rowIndex] = [];
    }
    // push to array instead of replacing
    this.tagPanelBreadcrumb[rowIndex] = [
      ...this.tagPanelBreadcrumb[rowIndex],
      { tagName: flatNode.tagName, attributeName: attribute, node },
    ];

    this.tagSearchQuery[rowIndex] = '';
    this.cdr.detectChanges();
  }

  drillUp(rowIndex: number) {
    this.tagPanelDepth[rowIndex] = Math.max((this.tagPanelDepth[rowIndex] ?? 1) - 1, 0);
    if (this.tagPanelBreadcrumb[rowIndex]?.length) {
      // pop last item instead of clearing
      this.tagPanelBreadcrumb[rowIndex] = this.tagPanelBreadcrumb[rowIndex].slice(0, -1);
    }
    this.tagSearchQuery[rowIndex] = '';
    this.cdr.detectChanges();
  }

  // ─── Tag selection ────────────────────────────────────────────────────────────

  isTagSelected(group: FormGroup, tagName: string): boolean {
    const selected: string[] = group.get('toppings')?.value || [];
    return selected.includes(tagName);
  }
  closeTagPanel(i: number): void {
    this.tagPanelDepth[i] = 0;
  }

  // KEY FIX: No debounce guard needed — (change) fires exactly once per interaction.
  // The old (click) on the row div caused double-toggle; it is now removed from the template.
  onPanelTagToggle(group: FormGroup, flatNode: FlatTagNode) {
    const control = group.get('toppings') as FormControl;
    const currentValue: string[] = [...(control.value || [])];
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);

    const node = attrObj ? this.findNodeByRootTagId(attrObj.tagTree, flatNode.rootTagId) : null;
    const allDescendants = node ? this.getAllDescendantNames(node) : [];
    const isSelected = currentValue.includes(flatNode.tagName);

    if (!isSelected) {
      if (currentValue.length >= 10) {
        this.snackbarService.failure(this.translate.instant('Only 10 tags can be selected'));
        return;
      }
      const getAncestorNames = (nodes: TagNode[], targetId: number, ancestors: string[] = []): string[] | null => {
        for (const n of nodes) {
          if (n.rootTagId === targetId) return ancestors;
          const found = getAncestorNames(n.children, targetId, [...ancestors, n.tagName]);
          if (found) return found;
        }
        return null;
      };
      const ancestorNames = attrObj ? getAncestorNames(attrObj.tagTree, flatNode.rootTagId) || [] : [];

      if (ancestorNames.some((a) => currentValue.includes(a))) {
        this.snackbarService.failure(
          this.translate.instant('parentTagAlreadySelected', {
            tagName: flatNode.tagName,
          })
        );
        return;
      }
      const alreadySelectedDescendant = allDescendants.find((d) => currentValue.includes(d));
      if (alreadySelectedDescendant) {
        this.snackbarService.failure(
          this.translate.instant('childTagAlreadySelected', {
            tagName: flatNode.tagName,
            childName: alreadySelectedDescendant,
          })
        );
        return;
      }
      control.setValue(Array.from(new Set([...currentValue, flatNode.tagName])), { emitEvent: false });
    } else {
      control.setValue(
        currentValue.filter((v) => v !== flatNode.tagName),
        { emitEvent: false }
      );
    }

    this.emitGroupRule();
    this.cdr.detectChanges();
  }

  clearAllTags(group: FormGroup) {
    const control = group.get('toppings') as FormControl;
    control.setValue([], { emitEvent: false });
    this.emitGroupRule();
    this.cdr.detectChanges();
  }

  getFooterText(group: FormGroup): string {
    const selected: string[] = group.get('toppings')?.value || [];
    if (!selected.length) return '';
    const last = selected[selected.length - 1]; // most recently selected
    const rest = selected.length - 1;
    return rest > 0 ? `${last} (+${rest} more)` : last;
  }

  removeChip(selected: string, group: FormGroup) {
    const control = group.get('toppings') as FormControl;
    const currentValue: string[] = control.value || [];
    const attribute = group.get('myControl')?.value;
    const attrObj = this.getAttrObj(attribute);
    const tagObj = attrObj?.tags.find((t: any) => t.tagName === selected);

    if (!tagObj) {
      control.setValue(currentValue.filter((v) => v !== selected));
      return;
    }

    const node = attrObj ? this.findNodeByRootTagId(attrObj.tagTree, tagObj.rootTagId) : null;
    const toRemove = new Set([selected, ...(node ? this.getAllDescendantNames(node) : [])]);
    control.setValue(currentValue.filter((v) => !toRemove.has(v)));
    this.emitGroupRule();
  }

  // ─── Add / clear / delete rows ────────────────────────────────────────────────

  addCondition() {
    this.formArray.push(this.createConditionGroup());
  }

  setConditionType(type: 'AND' | 'OR') {
    this.conditionType = type;
    this.openGroupConnectorIndex = -1;
    this.emitGroupRule();
  }

  onClickClear(index: number) {
    const group = this.formArray.at(index) as FormGroup;
    const control = group.get('myControl') as FormControl;

    group.reset({ myControl: '', toppings: [], subtags: [], availableTags: [], availableSubtags: [], flatTags: [] });

    group.patchValue({
      filteredOptions: control.valueChanges.pipe(
        startWith(''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      ),
    });

    this.tagPanelBreadcrumb[index] = null;
    this.tagSearchQuery[index] = '';
    this.updateSelectedAttributes();
    this.refreshFilters();
    this.emitGroupRule();
    this.snackbarService.success(this.translate.instant('Selection has been cleared.'));
  }

  onClickDelete(index: number) {
    if (this.formArray.length <= 1) return;

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '460px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      // Save snapshot for undo
      const deletedGroup = this.formArray.at(index) as FormGroup;
      const snapshot = {
        myControl: deletedGroup.get('myControl')?.value,
        toppings: [...(deletedGroup.get('toppings')?.value || [])],
        subtags: [...(deletedGroup.get('subtags')?.value || [])],
      };

      // Perform delete
      this.formArray.removeAt(index);
      this.updateSelectedAttributes();
      this.refreshFilters();
      this.emitGroupRule();

      // Snackbar with Undo
      const snackRef = this.snackBar.open(this.translate.instant('Condition deleted successfully'), this.translate.instant('Undo'), {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: 'undo-snackbar',
      });

      snackRef.onAction().subscribe(() => {
        const restoredGroup = this.createConditionGroup();

        if (snapshot.myControl) {
          restoredGroup.patchValue({ myControl: snapshot.myControl }, { emitEvent: false });

          const attrObj = this.getAttrObj(snapshot.myControl);
          if (attrObj) {
            this.expandedTags[index] = new Set<string>();
            const flatTags = this.flattenTreeWithExpansion(attrObj.tagTree, 0, index);
            restoredGroup.patchValue({ toppings: snapshot.toppings, subtags: snapshot.subtags, flatTags }, { emitEvent: false });
          }
        }

        this.formArray.insert(index, restoredGroup);
        this.updateSelectedAttributes();
        this.refreshFilters();
        this.emitGroupRule();
        this.cdr.detectChanges();
      });
    });
  }

  // ─── Filter helpers ───────────────────────────────────────────────────────────

  private updateSelectedAttributes() {
    this.selectedAttributes = this.formArray.controls.map((g) => g.get('myControl')?.value).filter((v) => !!v && this.options.includes(v));
  }

  private refreshFilters() {
    this.formArray.controls.forEach((group) => {
      const control = group.get('myControl') as FormControl;
      const obs = control.valueChanges.pipe(
        startWith(control.value || ''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      );
      group.patchValue({ filteredOptions: obs }, { emitEvent: false });
    });
  }

  private refreshFiltersExcept(excludeGroup: FormGroup) {
    this.formArray.controls.forEach((g) => {
      if (g === excludeGroup) return;
      const control = g.get('myControl') as FormControl;
      const obs = control.valueChanges.pipe(
        startWith(control.value || ''),
        distinctUntilChanged(),
        map((value: string) => this._filter(value || '', control))
      );
      g.patchValue({ filteredOptions: obs }, { emitEvent: false });
    });
  }

  private _filter(value: string, control: FormControl): string[] {
    const filterValue = value.toLowerCase();
    const otherSelectedAttributes = this.formArray.controls
      .filter((g) => g.get('myControl') !== control)
      .map((g) => g.get('myControl')?.value)
      .filter((v) => !!v);
    return this.options.filter((option) => option.toLowerCase().includes(filterValue) && !otherSelectedAttributes.includes(option));
  }

  // ─── Emit rule to parent ──────────────────────────────────────────────────────

  emitGroupRule() {
    if (this._isHydrating) return;

    const allSelectedNames: string[] = [];
    const rows = this.formArray.controls
      .map((group: FormGroup) => {
        const attributeName = group.get('myControl')?.value;
        const selectedTags = group.get('toppings')?.value || [];
        const attrObj = this.getAttrObj(attributeName);
        allSelectedNames.push(...selectedTags);
        if (!attrObj || !selectedTags.length) return null;
        const tagIds = selectedTags
          .map((tagName) => {
            const tag = attrObj.tags.find((t) => t.tagName === tagName);
            return tag?.tagId;
          })
          .filter(Boolean);
        return { attributeId: attrObj.attributeId, tagIds };
      })
      .filter(Boolean);

    this.groupRuleChange.emit({ groupOp: this.conditionType, rows, tagNames: allSelectedNames });
  }
  getPanelLevel(index: number): string {
    const depth = this.tagPanelDepth[index] ?? 0;
    const level = Math.min(depth + 1, 10); // cap at level 10
    return level < 10 ? '0' + level : '' + level;
  }

  selectConditionType(type: 'AND' | 'OR') {
    this.selectedConditionType = type;
    this.isAddConditionMenuOpen = false;
    this.setConditionType(type);
    this.addCondition();
  }

  toggleAddConditionMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isAddConditionMenuOpen = !this.isAddConditionMenuOpen;
  }
  toggleGroupConnectorMenu(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.openGroupConnectorIndex = this.openGroupConnectorIndex === index ? -1 : index;
  }
}
